"use client";
import { Track } from "@/src/lib/tracking";
/**
 * LoginModalProvider — 基于新 auth 模块的精简版
 *
 * 职责：仅管理登录弹窗的可见性 + 触发登录后跳转/回调
 * 登录状态由 AuthOrchestrator 管理（features/auth）
 *
 * 旧版保留为 LoginModalProvider.legacy.tsx，供回退参考
 */
import {
  type Context,
  createContext,
  useCallback,
  useRef,
  useState,
} from "react";
import { useMemoizedFn } from "ahooks";
import { useAuth } from "@features/auth";
import type { AuthProvider } from "@features/auth/types";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { EmailLoginModal } from "@components/EmailLogin";
import {
  type LoginOpenOptions,
  type LoginPostAction,
  type ILoginModalContext,
  type ILoginProviderProps,
} from "./types";
import { LoginStore } from "@store/modules/login";
import { rootStore } from "@store/rootStore";
import type { Web3LoginSupportedType } from "@features/auth/compat/sdk-types";
import { useOnboardingCheckoutReturn } from "@hooks/onboarding/useOnboardingCheckoutReturn";
import { useOnboardingEntryGate } from "@hooks/onboarding/useOnboardingEntryGate";
import type { AuthSigninMethod } from "@hooks/onboarding/authSigninEntry";
import { trackAuthSigninSuccess } from "@hooks/onboarding/onboardingAnalytics";
import { resolveIntroAuthPlatform } from "@components/SignInIntro/resolveIntroAuthPlatform";
import { resolveOverlapSigninExtras } from "@features/etf-overlap/tracking/authSigninSuccess";
import { CheckoutConfirmingOverlay } from "@components/Member/CheckoutConfirming/CheckoutConfirmingOverlay";

const LoginModalContext: Context<ILoginModalContext> =
  createContext<ILoginModalContext>({});

const PROVIDER_MAP: Partial<Record<Web3LoginSupportedType, AuthProvider>> = {
  APPLE: "apple",
  GOOGLE: "google",
  TWITTER: "twitter",
};

/**
 * `auth_signin_success` 的 `method` 与 Intro / 登录页同口径（否则漏斗按 method 拆不到一起）。
 *
 * twitter 在弹窗 UI 里当前不可达（`SocialLoginButtons` 的 `SOCIAL_ICONS` 只有 Apple/Google），
 * 但 [PROVIDER_MAP] 支持它 —— 列在这里而不是让它落空，否则真走到那条路径时会**静默丢一条成功**，
 * 且与 `screens/LoginPage` 的 `mapLoginTypeToMethod`（把未知 provider 归成 email）口径相反。
 */
const SIGNIN_METHOD_MAP: Partial<
  Record<Web3LoginSupportedType, AuthSigninMethod>
> = {
  APPLE: "apple",
  GOOGLE: "google",
  TWITTER: "twitter",
};

export const LoginModalProvider = ({ children }: ILoginProviderProps) => {
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [hideCloseButton, setHideCloseButton] = useState(false);
  const [closeNavigateTo, setCloseNavigateTo] = useState<string | undefined>();
  const { isLoading, login } = useAuth();
  const router = useShellRouter();

  const pendingActionRef = useRef<{
    action: LoginPostAction;
    createdAt: number;
    expiresAt: number;
    consumed: boolean;
  } | null>(null);

  const showLoginModal = useMemoizedFn((options?: LoginOpenOptions) => {
    if (options?.hideCloseButton) {
      setHideCloseButton(true);
    }
    if (options?.closeNavigateTo) {
      setCloseNavigateTo(options.closeNavigateTo);
    }
    if (options?.postLoginAction) {
      const expireMs = options.expireMs ?? 300_000;
      pendingActionRef.current = {
        action: options.postLoginAction,
        createdAt: Date.now(),
        expiresAt: Date.now() + expireMs,
        consumed: false,
      };
    }
    setLoginModalVisible(true);
  });

  const closeLoginModal = useCallback(() => {
    setLoginModalVisible(false);
    setHideCloseButton(false);
    if (closeNavigateTo) {
      router.replace(closeNavigateTo);
      setCloseNavigateTo(undefined);
    }
  }, [closeNavigateTo, router]);

  const { isConfirming: isOnboardingCheckoutConfirming } =
    useOnboardingCheckoutReturn();
  useOnboardingEntryGate({ closeLoginModal });

  const executePostAction = useMemoizedFn(async (action: LoginPostAction) => {
    switch (action.type) {
      case "navigate":
        action.replace ? router.replace(action.to) : router.push(action.to);
        break;
      case "navigate_dynamic": {
        const ctx = {
          ownerInfo: rootStore.user.ownerInfo,
          eoaAddress: LoginStore.eoaAddress,
          loginType: LoginStore.loginType,
          isLoggedIn: LoginStore.isLoggedIn,
        };
        const to = await action.toBuilder(ctx);
        if (to) {
          action.replace ? router.replace(to) : router.push(to);
        } else if (action.fallbackTo) {
          router.replace(action.fallbackTo);
        }
        break;
      }
      case "callback":
        await action.run();
        break;
      case "composed":
        for (const sub of action.actions) {
          await executePostAction(sub);
        }
        break;
    }
  });

  const handleLoginSuccess = useMemoizedFn(() => {
    setLoginModalVisible(false);
    const pending = pendingActionRef.current;
    if (pending && !pending.consumed && Date.now() < pending.expiresAt) {
      pending.consumed = true;
      executePostAction(pending.action);
    }
  });

  const loginController = useMemoizedFn(
    async (type: Web3LoginSupportedType) => {
      const provider = PROVIDER_MAP[type];
      if (!provider) {
        return;
      }

      try {
        await login(provider, { forceSelect: true });
        // 弹窗路径的登录成功此前**完全没有上报** —— `auth_signin_success` 只写在独立登录页
        // (`screens/LoginPage.tsx`)，于是「从付费墙/收藏等位置弹窗登录」这条漏斗只有点击没有成功。
        const method = SIGNIN_METHOD_MAP[type];
        if (method) {
          const base = {
            platform: resolveIntroAuthPlatform(),
            method,
            entry: "login_modal",
          };
          const extras = resolveOverlapSigninExtras();
          if (extras) {
            void extras.then((extra) =>
              trackAuthSigninSuccess({ ...base, ...extra }),
            );
          } else {
            trackAuthSigninSuccess(base);
          }
        }
        handleLoginSuccess();
      } catch (error) {
        Track.log("error", "LoginModalV2", "login failed", {
          provider,
          cause: String(error),
        });
      }
    },
  );

  return (
    <LoginModalContext.Provider
      value={{
        open: showLoginModal,
        loginModalVisible,
      }}
    >
      {children}
      <EmailLoginModal
        open={loginModalVisible}
        onClose={closeLoginModal}
        onSocialClick={loginController}
        loading={isLoading}
        hideCloseButton={hideCloseButton}
        closeBehavior={closeNavigateTo ? "direct" : "default"}
      />
      {/* onboarding 首付费墙 AIRWALLEX 回跳的确认遮罩（定价页回跳由 Member.tsx 自己渲染） */}
      <CheckoutConfirmingOverlay open={isOnboardingCheckoutConfirming} />
    </LoginModalContext.Provider>
  );
};
